/**
 * Signer Acknowledgment Component
 *
 * Modal displayed before signing to verify signer identity.
 * Features:
 * - Signer info display (name, email)
 * - Document info display (name, date)
 * - Terms and conditions checkbox
 * - Confirm/Decline buttons
 * - Mobile-first responsive design
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, User, FileText } from 'lucide-react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
} from '@respectly/ui';

interface SignerAcknowledgmentProps {
  isOpen: boolean;
  onConfirm: () => void;
  onNotMe: () => void;
  signerInfo: {
    name: string;
    email?: string;
  };
  documentInfo: {
    name: string;
  };
  termsUrl?: string;
}

export function SignerAcknowledgment({
  isOpen,
  onConfirm,
  onNotMe,
  signerInfo,
  documentInfo,
  termsUrl = 'https://www.respectly.ai/en/terms/',
}: SignerAcknowledgmentProps) {
  const [termsAccepted, setTermsAccepted] = useState(false);

  const todayDate = new Date().toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <Dialog open={isOpen}>
      <DialogContent preventClose showCloseButton={false} className="max-w-md">
        <div className="space-y-6">
          {/* Warning Banner */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4"
          >
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm font-medium text-gray-900">
              Please review your data and start signing
            </p>
          </motion.div>

          {/* Info Sections - Two columns on larger screens */}
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Signer Info */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="min-w-0 space-y-3"
            >
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900">
                  Signer Info
                </h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="min-w-0 break-words">
                  <span className="text-gray-500">Name: </span>
                  <span className="font-medium text-gray-900">
                    {signerInfo.name}
                  </span>
                </div>
                {signerInfo.email && (
                  <div className="min-w-0 break-words">
                    <span className="text-gray-500">Email: </span>
                    <span className="font-medium text-gray-900">
                      {signerInfo.email}
                    </span>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Document Info */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="min-w-0 space-y-3"
            >
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900">Form</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="min-w-0 break-words">
                  <span className="text-gray-500">Name: </span>
                  <span className="font-medium text-gray-900">
                    {documentInfo.name}
                  </span>
                </div>
                <div className="min-w-0 break-words">
                  <span className="text-gray-500">Todays Date: </span>
                  <span className="font-medium text-gray-900">{todayDate}</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200" />

          {/* Terms Checkbox */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Checkbox
              checked={termsAccepted}
              onCheckedChange={(checked) => setTermsAccepted(!!checked)}
              label={
                <span className="text-sm text-gray-700">
                  I confirm that I read and agree to the{' '}
                  <a
                    href={termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms and Conditions
                  </a>
                  .
                </span>
              }
            />
          </motion.div>

          {/* Action Buttons */}
          <DialogFooter className="pt-2">
            <Button
              variant="outline"
              onClick={onNotMe}
              className="flex-1 sm:flex-none"
            >
              Not Me
            </Button>
            <Button
              variant="default"
              onClick={onConfirm}
              disabled={!termsAccepted}
              className="flex-1 sm:flex-none"
            >
              Yes, It's Me
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
